/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore, useClientEnvVarsStore } from '@marriott/mi-store-utils'; // Your utility file
import { encryptChatHistory, decryptChatHistory, chatStorageKey } from '../utils/chatEncryptionUtils';
import { isStorageError } from '../utils/storageUtils';
import { idbGet, idbSet, idbDelete } from '../utils/indexedDBStorage';
import { LoadingMessage, MIN_PHASE_DISPLAY_DURATION_MS } from '../utils/loadingMessageUtils';
import { redact } from '../utils/sensitiveDataRedactor';
import {
  syncConversationStateToGlobalStores,
  shouldSyncOnConversationSwitch,
  clearFingerprintCache,
  hasNonDefaultState,
} from '../utils/storeSyncUtils';
import { addChatSearchToRecentSearches } from '../utils/recentSearchStorage';

// Fixed consumer ID for local development testing
const LOCAL_DEV_CONSUMER_ID = 'local_test_id';

/**
 * Get the local dev consumer ID with runtime safety guard.
 * Throws an error if used outside localhost to prevent accidental production use (F3).
 */
function getLocalDevConsumerId(): string {
  if (typeof window !== 'undefined' && !['localhost', '127.0.0.1', ''].includes(window.location.hostname)) {
    throw new Error(
      'LOCAL_DEV_CONSUMER_ID cannot be used outside localhost. ' +
        'This is a security measure to prevent shared encryption keys in production.'
    );
  }
  return LOCAL_DEV_CONSUMER_ID;
}

function uuidv4() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
  );
}

// Default chat expiration in days (fallback when env var is not set)
const DEFAULT_CHAT_EXPIRATION_DAYS = 5;

/**
 * Get the chat expiration date based on the CHAT_EXPIRATION_DAYS environment variable.
 * Falls back to DEFAULT_CHAT_EXPIRATION_DAYS (5 days) if not set.
 */
function getChatExpirationDate(): Date {
  const envExpirationDays = useClientEnvVarsStore.getState().envVarsObject['CHAT_EXPIRATION_DAYS'];
  const expirationDays = envExpirationDays ? Number(envExpirationDays) : DEFAULT_CHAT_EXPIRATION_DAYS;
  return new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000);
}

// Enhanced message interface with navigation capabilities
export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  toolsUsed?: string[];
  intent?: string;
  uiType?: string;
  // Enhanced navigation properties
  hasPreview?: boolean;
  previewData?: {
    viewType: ViewType;
    data: ViewData;
    title: string;
    itemCount?: number;
  };
  navigationButton?: {
    label: string;
    action: () => void;
    variant?: 'primary' | 'secondary' | 'outline';
    icon?: string;
  };
  uiData?: any;
  /** Persisted with chat history (encrypted IndexedDB). Set when user chooses thumbs; cannot be changed after. */
  thumbFeedback?: 'up' | 'down';
}

/** `intent` value for the synthetic message added when the user cancels an in-flight request. */
export const MESSAGE_INTENT_USER_CANCELLED_REQUEST = 'cancelled_by_user';

/** Visible copy for the synthetic assistant row when the user stops an in-flight request (`cancelRequest`). */
export const USER_CANCELLED_REQUEST_MESSAGE_COPY = 'Request cancelled by user.';

/** Synthetic cancel row from store (`intent`), or legacy persisted rows that match copy only (no `intent`). */
export function isUserCancelledAssistantMessage(message: {
  isUser: boolean;
  intent?: string;
  content?: string;
}): boolean {
  if (message.isUser) return false;
  if (message.intent === MESSAGE_INTENT_USER_CANCELLED_REQUEST) return true;
  return message.content?.trim() === USER_CANCELLED_REQUEST_MESSAGE_COPY;
}

// Per-conversation search criteria (mini search bar state)
export interface ConversationSearchCriteria {
  checkInDate?: string;
  checkOutDate?: string;
  flexible?: boolean;
  numRooms: number;
  numAdultsPerRoom: number;
  numChildrenPerRoom: number;
  childrenAges: number[];
  useRewardsPoints: boolean;
  clusterCode?: string;
  corporateCode?: string;
  groupCode?: string;
}

// Per-conversation filter state
export interface ConversationFilterState {
  brands: string[];
  amenities: string[];
  priceRange: { min?: number; max?: number };
  pointsRange: { min?: number; max?: number };
  distance?: number;
  hotelTypes: string[];
  activities: string[];
  transportationTypes: string[];
  events?: string[];
  includeTaxesAndFees?: boolean;
  states?: string[];
  cities?: string[];
}

// Per-conversation location state (from geoCodeInfo)
export interface ConversationLocationState {
  placeId?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  stateProvince?: string;
  country?: string;
  address?: string;
  destination?: string;
  destinationType?: string;
  region?: string;
}

// Combined per-conversation search state
export interface ConversationSearchState {
  searchCriteria: ConversationSearchCriteria;
  filterState: ConversationFilterState;
  locationState?: ConversationLocationState;
  searchResultsMeta?: {
    total: number;
    query?: string;
    llmMetadata?: any;
  };
  /**
   * Tracks whether user has explicitly modified search parameters via NLS FE endpoint.
   * Used to determine if ui_context should be sent with the next message.
   */
  hasUserModifiedSearch?: boolean;
}

// Default values for search criteria
export const DEFAULT_SEARCH_CRITERIA: ConversationSearchCriteria = {
  numRooms: 1,
  numAdultsPerRoom: 1,
  numChildrenPerRoom: 0,
  childrenAges: [],
  useRewardsPoints: false,
};

// Default values for filter state
export const DEFAULT_FILTER_STATE: ConversationFilterState = {
  brands: [],
  amenities: [],
  priceRange: {},
  pointsRange: {},
  hotelTypes: [],
  activities: [],
  transportationTypes: [],
  events: [],
  includeTaxesAndFees: false,
  states: [],
  cities: [],
};

// Default values for location state
export const DEFAULT_LOCATION_STATE: ConversationLocationState = {};

// New Chat Session Interface
export interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
  isActive?: boolean;
  summary?: string;
  messageCount: number;
  expiresAt: Date;

  // TIP session identifiers (per chat)
  tipWorkflowId?: string;
  tipStreamUrl?: string;
  lastEventId?: string; // Last SSE event ID for reconnection (Last-Event-Id header)

  // Latest error message ID for retry functionality
  latestErrorMessageId?: string | null;

  // Per-chat processing state (not persisted - transient)
  isProcessing?: boolean;
  processingStatus?: AgentStatus;
  loadingMessage?: LoadingMessage | null;

  // Chat disabled state (e.g., due to guardrail limit exceeded)
  isDisabled?: boolean;

  // Per-conversation search state (search criteria + filters)
  conversationSearchState?: ConversationSearchState;
}

export interface AgentStatus {
  status: 'thinking' | 'searching' | 'analyzing' | 'responding' | 'complete' | 'idle';
  action?: string;
}

export interface IntentData {
  intent: string;
  confidence: number;
  changed: boolean;
  extractedParams?: Record<string, any>;
}

export interface ViewData {
  restaurants?: any[];
  attractions?: any[];
  totalResults?: number;
  searchQuery?: string;
  filters?: Record<string, any>;
  rawData?: any; // For PropertyCardComponent integration
  selectedItem?: any;
  /** True when response contains a single property (e.g., availability_calendar_search) */
  isSinglePropertyResponse?: boolean;
  /** ampRequest snapshot for this specific message - used when clicking property cards */
  ampRequest?: any;
  /** True when this response was produced from a flexible-date search */
  isFlexDate?: boolean;
  [key: string]: any;
}

export interface AgentExecutionResult {
  response: string;
  toolsUsed: string[]; // Array of tool names (e.g., ["nls-hotel-search"])
  intent: string;
  viewType?: string;
  viewData?: ViewData;
  uiData?: any;
  uiType?: string;
  metadata: {
    executionTime: number;
    tokenUsage?: {
      prompt: number;
      completion: number;
      total: number;
    };
  };
  chatId?: string;
  lastEventId?: string;
}

// Enhanced view types matching your implementation
export type ViewType =
  | 'hotel-list'
  | 'hotel-detail'
  | 'dining'
  | 'attractions'
  | 'booking'
  | 'map'
  | 'empty'
  | 'booking_management'; // Match your API response

export type ViewMode = 'grid' | 'map';

export interface PreviewState {
  id: string;
  viewType: ViewType;
  viewMode: ViewMode;
  data: ViewData;
  title: string;
  timestamp: Date;
  intent?: string;
  metadata?: Record<string, any>;
}

export interface NavigationHistoryItem {
  state: PreviewState;
  action: string;
  timestamp: Date;
}

export interface MockProfileData {
  name: string;
  loyaltyLevel: string;
  loyaltyCode: string;
  points: number;
  lifetimeNights: number;
}

export interface Experiments {
  useMockData: boolean;
  enableSSELogging: boolean;
  enableIntentLogging: boolean;
  useEnhancedMemory: boolean;
  enableSafetyBypass: boolean;
  enableAgentTracing: boolean;
  useAlternativeAPI: boolean;
  enableStreamingDebug: boolean;
  showTokenUsage: boolean;
  useNLSSearch: boolean; // This is the key one for NLS
  useNLSUXL: boolean; // Use NLS UXL GraphQL endpoint
  usePlatformAPI: boolean; // Use external platform API instead of internal orchestrator
}

// Main store interface matching your existing patterns
export interface MarriottAIPreviewStore {
  // Current state - matches your existing state structure
  currentState: PreviewState;
  consumerId: string | null;
  authenticated: boolean;
  _hasHydrated: boolean; // Flag to track if IndexedDB hydration has completed
  // Navigation history
  history: NavigationHistoryItem[];
  historyIndex: number;
  maxHistorySize: number;

  // UI state - reusing your existing patterns
  isLoading: boolean;
  isProcessing: boolean;
  isNewSearchResult: boolean;
  status: AgentStatus;

  // Dynamic loading message driven by node_planning_completed SSE events
  loadingMessage: LoadingMessage | null;
  pendingLoadingMessage: LoadingMessage | null;
  loadingMessageTimer: ReturnType<typeof setTimeout> | null;

  hoveredHotelId: string | null; // Matches your hoveredHotelId
  selectedItemId: string | null;
  currentIntent: string | null; // Matches your currentIntent

  // Enhanced Chat Session Management
  chatSessions: ChatSession[];
  currentChatId: string | null;
  messages: Message[]; // Current chat messages
  inputValue: string;

  // Mobile/responsive state
  showChat: boolean;
  isMobile: boolean;
  isSidebarOpen: boolean;
  isNewChatMode: boolean;
  isSettingsOpen: boolean;
  // Personalization state
  usePersonalization: boolean;
  selectedMemberLevel: string | null;
  mockProfileData: MockProfileData | null;
  fullProfileData: any | null; // Complete profile data from all GraphQL queries
  userPreferences: any | null; // Extracted preferences
  recentActivity: any | null; // Past bookings and stays
  personalizedSuggestions: string[]; // Array of suggestion strings
  propertyMediaById: Record<string, any>;
  // Dev experiments
  experiments: Experiments;

  // Sign-in drawer model (non-persisted, extracted from AEM page model)
  signInDrawerModel: any | null;

  // Actions for API integration
  handleAPIResponse: (result: AgentExecutionResult) => void;
  updateViewFromIntent: (intent: string, data?: ViewData) => void;

  // Navigation actions
  navigateToView: (viewType: ViewType, data?: ViewData, metadata?: Record<string, any>) => void;
  updateViewData: (data: Partial<ViewData>) => void;
  updateViewMode: (mode: ViewMode) => void;
  goBack: () => boolean;
  goForward: () => boolean;
  canGoBack: () => boolean;
  canGoForward: () => boolean;
  clearHistory: () => void;
  setConsumerId: (id: string) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;

  // Item interaction - matching your existing patterns
  setHoveredHotelId: (hotelId: string | null) => void;
  setSelectedItem: (itemId: string | null, itemData?: any) => void;

  // Loading and status - matching your existing patterns
  setLoading: (loading: boolean) => void;
  setIsProcessing: (processing: boolean, chatId?: string) => void;
  setIsNewSearchResult: (isNew: boolean) => void;
  setStatus: (status: AgentStatus, chatId?: string) => void;

  // Dynamic loading message actions
  enqueueLoadingMessage: (message: LoadingMessage, chatId?: string) => void;
  clearLoadingMessage: (chatId?: string) => void;

  // Per-chat processing state management
  setChatProcessingState: (
    chatId: string,
    isProcessing: boolean,
    status?: AgentStatus,
    loadingMessage?: LoadingMessage | null
  ) => void;

  // Cancel request action
  cancelRequest: () => void;

  // Enhanced Chat Session Actions
  createNewChat: () => string;
  switchToChat: (chatId: string) => void;
  deleteChat: (chatId: string) => void;
  updateChatTitle: (chatId: string, title: string) => void;
  duplicateChat: (chatId: string) => string | null;
  getCurrentChat: () => ChatSession | null;
  setChatTipSession: (chatId: string, tip: { workflowId: string; streamUrl: string }) => void;
  clearChatTipSession: (chatId: string) => void;
  setLastEventId: (chatId: string, eventId: string) => void;
  setLatestErrorMessageId: (chatId: string, errorMessageId: string | null) => void;
  setChatDisabled: (chatId: string, disabled: boolean) => void;

  // Per-conversation search state actions
  setConversationSearchCriteria: (chatId: string, criteria: Partial<ConversationSearchCriteria>) => void;
  setConversationFilterState: (chatId: string, filters: Partial<ConversationFilterState>) => void;
  setConversationLocationState: (chatId: string, location: Partial<ConversationLocationState>) => void;
  getConversationSearchState: (chatId: string) => ConversationSearchState | undefined;
  setConversationSearchModified: (chatId: string, modified: boolean) => void;
  getConversationSearchModified: (chatId: string) => boolean;

  // Chat integration
  addMessage: (message: Message) => void;
  addMessageToChat: (chatId: string, message: Message) => void;
  setMessageThumbFeedback: (chatId: string, messageId: string, feedback: 'up' | 'down') => void;
  removeMessage: (messageId: string) => void;
  addAIMessageWithNavigation: (
    content: string,
    viewType: ViewType,
    viewData: ViewData,
    toolsUsed?: string[],
    intent?: string
  ) => void;
  navigateToMessagePreview: (messageId: string) => void;
  setInputValue: (value: string) => void;
  clearMessages: () => void; // Clear current chat messages

  // UI state management
  setShowChat: (show: boolean) => void;
  setIsMobile: (mobile: boolean) => void;
  setIsSidebarOpen: (open: boolean) => void;
  setIsNewChatMode: (isNewChatMode: boolean) => void;
  setIsSettingsOpen: (open: boolean) => void;

  // Utility actions
  reset: () => void;
  getCurrentViewTitle: () => string;
  getCurrentItemCount: () => number;

  // New Chat Management Utilities
  generateChatTitle: (firstMessage: string) => string;
  getRecentChats: (limit?: number) => ChatSession[];
  searchChats: (query: string) => ChatSession[];

  // Experiments actions
  toggleExperiment: (key: string) => void;
  setExperiments: (experiments: Partial<Experiments>) => void;
  getActiveExperiments: () => string[];

  // Personalization actions
  togglePersonalization: () => void;
  setSelectedMemberLevel: (level: string | null) => void;
  setMockProfileData: (data: MockProfileData | null) => void;
  loadUserProfile: (memberLevel: string, client: any) => Promise<void>;
  setFullProfileData: (data: any) => void;
  setUserPreferences: (preferences: any) => void;
  setRecentActivity: (activity: any) => void;
  setPersonalizedSuggestions: (suggestions: string[]) => void;
  setPropertyMediaById: (propertiesMedia: any[]) => void;
  clearPropertyMediaById: () => void;

  // Sign-in drawer model action
  setSignInDrawerModel: (model: any | null) => void;
}

// Default state matching your initial implementation
const createInitialState = (): PreviewState => ({
  id: 'initial',
  viewType: 'empty',
  viewMode: 'map', // Default to map as in your implementation
  data: {},
  title: 'Your Next Trip Starts Here',
  timestamp: new Date(),
});

// Store creator with your existing patterns
const createMarriottAIPreviewStore: StateCreator<MarriottAIPreviewStore> = (set, get) => ({
  // Initial state
  currentState: createInitialState(),
  history: [],
  historyIndex: -1,
  maxHistorySize: 50,

  // UI state matching your implementation
  isLoading: false,
  isProcessing: false,
  isNewSearchResult: false,
  status: { status: 'idle' },
  loadingMessage: null,
  pendingLoadingMessage: null,
  loadingMessageTimer: null,
  hoveredHotelId: null,
  selectedItemId: null,
  currentIntent: null,

  // Enhanced Chat Session Management
  chatSessions: [],
  currentChatId: null,
  messages: [],
  inputValue: '',

  // Mobile/responsive state
  showChat: true, // Default to chat view (especially important for mobile)
  isMobile: false,
  isSidebarOpen: false,
  isNewChatMode: false,
  isSettingsOpen: false,

  // Personalization state
  usePersonalization: false,
  selectedMemberLevel: null,
  mockProfileData: null,
  fullProfileData: null,
  userPreferences: null,
  recentActivity: null,
  personalizedSuggestions: [],
  propertyMediaById: {},
  consumerId: null,
  authenticated: false,
  _hasHydrated: false, // Will be set to true after IndexedDB hydration completes
  experiments: {
    useMockData: false,
    enableSSELogging: false,
    enableIntentLogging: false,
    useEnhancedMemory: false,
    enableSafetyBypass: false,
    enableAgentTracing: false,
    useAlternativeAPI: false,
    enableStreamingDebug: false,
    showTokenUsage: false,
    usePlatformAPI: true,
    useNLSSearch: false,
    useNLSUXL: false,
  },

  // Sign-in drawer model (non-persisted)
  signInDrawerModel: null,

  // Personalization actions
  togglePersonalization: () => {
    set(state => {
      state.usePersonalization = !state.usePersonalization;
      // Clear profile data when disabling personalization
      if (!state.usePersonalization) {
        state.mockProfileData = null;
      }
      return state;
    }, false);
  },
  setConsumerId: (id: string) => {
    const currentId = get().consumerId;
    set(state => {
      state.consumerId = id;
      return state;
    });

    const effectiveId =
      id ||
      (useClientEnvVarsStore.getState().envVarsObject['IS_LOCAL_DEV'] === 'true' ? getLocalDevConsumerId() : null);

    if (effectiveId && (currentId !== id || !hasHydrated)) {
      hasHydrated = false;
      lastWrittenValue = null;
      useMarriottAIPreviewStore.persist.rehydrate();
    }
  },
  setIsAuthenticated: (isAuthenticated: boolean) => {
    set(state => {
      state.authenticated = isAuthenticated;
      return state;
    });
  },
  setSelectedMemberLevel: (level: string | null) => {
    set(state => {
      state.selectedMemberLevel = level;
      return state;
    }, false);
  },

  setMockProfileData: (data: MockProfileData | null) => {
    set(state => {
      state.mockProfileData = data;
      return state;
    }, false);
  },

  loadUserProfile: async (memberLevel: string, client: any) => {
    try {
      const { loadAllUserProfile } = await import('../services/profileGraphQLService');
      const { analyzeProfileAndGenerateSuggestions } = await import('../utils/preferenceAnalyzer');
      const profileData = await loadAllUserProfile(memberLevel, client);
      const suggestions = analyzeProfileAndGenerateSuggestions(profileData);
      set(state => {
        state.fullProfileData = profileData;
        state.personalizedSuggestions = suggestions;
        return state;
      }, false);
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  },

  setFullProfileData: (data: any) => {
    set(state => {
      state.fullProfileData = data;
      return state;
    }, false);
  },

  setUserPreferences: (preferences: any) => {
    set(state => {
      state.userPreferences = preferences;
      return state;
    }, false);
  },

  setRecentActivity: (activity: any) => {
    set(state => {
      state.recentActivity = activity;
      return state;
    }, false);
  },

  setPersonalizedSuggestions: (suggestions: string[]) => {
    set(state => {
      state.personalizedSuggestions = suggestions;
      return state;
    }, false);
  },
  setPropertyMediaById: (propertiesMedia: any[]) => {
    set(state => {
      const nextMediaById = { ...state.propertyMediaById };
      (propertiesMedia || []).forEach((item: any) => {
        const propertyId = item?.id;
        if (propertyId) {
          nextMediaById[propertyId] = item;
        }
      });
      state.propertyMediaById = nextMediaById;
      return state;
    }, false);
  },
  clearPropertyMediaById: () => {
    set(state => {
      state.propertyMediaById = {};
      return state;
    }, false);
  },

  // Sign-in drawer model action (non-persisted)
  setSignInDrawerModel: (model: any | null) => {
    set(state => {
      state.signInDrawerModel = model;
      return state;
    }, false);
  },

  // Handle API response - FIXED: Now properly syncs messages to chat session and handles per-chat state
  handleAPIResponse: result => {
    set(state => {
      const targetChatId = result?.chatId;
      const isActiveChat = targetChatId === state.currentChatId;

      const aiMessage: Message = {
        id: Date.now().toString() + '-ai',
        content: result.response,
        isUser: false,
        timestamp: new Date(),
        toolsUsed: result.toolsUsed,
        intent: result.intent,
        uiType: result.uiType,
        hasPreview: false,
        uiData: result.uiData,
      };

      // Build preview data if view data is provided
      let newState: PreviewState | null = null;
      if (result.viewType && result.viewData) {
        newState = {
          id: `${result.viewType}-${Date.now()}`,
          viewType: result.viewType as ViewType,
          viewMode: state.currentState.viewMode,
          data: result.viewData,
          title: generateViewTitle(result.viewType as ViewType, result.viewData),
          timestamp: new Date(),
          intent: result.intent,
          metadata: result.metadata,
        };
        const itemCount = getItemCountForViewType(result.viewType as ViewType, result.viewData);
        const navigationButton = createNavigationButton(result.viewType as ViewType, result.viewData, itemCount);

        aiMessage.hasPreview = true;
        aiMessage.previewData = {
          viewType: result.viewType as ViewType,
          data: result.viewData,
          title: generateViewTitle(result.viewType as ViewType, result.viewData),
          itemCount,
        };
        aiMessage.navigationButton = navigationButton;

        // Extract geoCodeInfo from search results and store in conversation location state
        if (result.viewType === 'hotel-list' && targetChatId) {
          const geoCodeInfo =
            result.viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.geoCodeInfo;

          // Save to recent searches for personalization (skip if geoCodeInfo is null - multi-location searches)
          if (geoCodeInfo) {
            const ampRequest =
              result.viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.ampRequest;
            addChatSearchToRecentSearches(geoCodeInfo, ampRequest);
          }

          if (geoCodeInfo) {
            const targetChat = state.chatSessions.find(c => c.id === targetChatId);
            if (targetChat) {
              if (!targetChat.conversationSearchState) {
                targetChat.conversationSearchState = {
                  searchCriteria: { ...DEFAULT_SEARCH_CRITERIA },
                  filterState: { ...DEFAULT_FILTER_STATE },
                };
              }
              const locationCity = geoCodeInfo.details?.location?.city ?? undefined;
              const locationState = geoCodeInfo.details?.location?.state ?? undefined;
              const locationCountry = geoCodeInfo.details?.location?.country ?? undefined;

              // Build destination string from city, state, country
              let destination: string | undefined;
              if (locationCity && locationState) {
                destination = `${locationCity}, ${locationState}`;
              } else if (locationCity) {
                destination = locationCity;
              }

              targetChat.conversationSearchState.locationState = {
                placeId: geoCodeInfo.placeId ?? undefined,
                latitude: geoCodeInfo.details?.location?.latitude ?? undefined,
                longitude: geoCodeInfo.details?.location?.longitude ?? undefined,
                city: locationCity,
                stateProvince: locationState,
                country: locationCountry,
                address: geoCodeInfo.details?.location?.address ?? undefined,
                destinationType: geoCodeInfo.details?.destinationType ?? undefined,
                destination,
                region: destination,
              };
            }
          }
        }
      }

      // Find or create target chat and add message
      let targetChat = state.chatSessions.find(c => c.id === targetChatId);
      if (!targetChat && targetChatId) {
        const newChat: ChatSession = {
          id: targetChatId,
          title: 'New Chat',
          messages: [],
          createdAt: new Date(),
          updatedAt: new Date(),
          messageCount: 0,
          expiresAt: getChatExpirationDate(),
        };
        state.chatSessions.push(newChat);
        targetChat = newChat;
      }

      // Always add message to the target chat session
      if (targetChat) {
        // Check if this is the first search result (for map animation)
        // messageCount <= 1 means only user message exists before this AI response
        const isFirstSearchResult = targetChat.messageCount <= 1;

        if (!targetChat.messages) {
          targetChat.messages = [];
        }
        targetChat.messages.push(aiMessage);
        targetChat.messageCount++;
        targetChat.updatedAt = new Date();
        // Clear processing state on the target chat
        targetChat.isProcessing = false;
        targetChat.processingStatus = { status: 'idle' };
        targetChat.loadingMessage = null;
        // Persist lastEventId if provided (for SSE reconnection)
        if (result.lastEventId) {
          targetChat.lastEventId = result.lastEventId;
        }

        // Mark new search result for map animation (only for first search in chat)
        if (result.viewType === 'hotel-list' && isFirstSearchResult && isActiveChat) {
          state.isNewSearchResult = true;
        }
      }

      // Only update global UI state if this is the active chat
      if (isActiveChat) {
        // Update intent if changed
        if (result.intent !== state.currentIntent) {
          state.currentIntent = result.intent;
        }

        // Update view/preview state
        if (newState) {
          // Add to history
          if (state.currentState.id !== 'initial') {
            const historyItem: NavigationHistoryItem = {
              state: state.currentState,
              action: `api-response-${result.viewType}`,
              timestamp: new Date(),
            };

            const newHistory = [...state.history.slice(0, state.historyIndex + 1), historyItem];
            if (newHistory.length > state.maxHistorySize) {
              newHistory.shift();
            }

            state.history = newHistory;
            state.historyIndex = newHistory.length - 1;
          }

          state.currentState = newState;
        }

        // Handle booking_management intent
        if (result.intent === 'booking_management') {
          state.currentState.viewType = 'booking';
        }

        // Add to current messages array (for active chat display)
        state.messages.push(aiMessage);

        // Reset interaction state
        state.hoveredHotelId = null;
        state.selectedItemId = null;
      }
      // If not active chat, message is stored in targetChat.messages but not in state.messages
      // User will see it when they switch back to that chat

      return state;
    }, false);
  },

  // Update view from intent - helper for intent-based navigation
  updateViewFromIntent: (intent, data = {}) => {
    set(state => {
      let viewType: ViewType = 'empty';

      switch (intent) {
        case 'hotel_search':
        case 'accommodation_search':
          viewType = 'hotel-list';
          break;
        case 'restaurant_search':
        case 'dining':
          viewType = 'dining';
          break;
        case 'attraction_search':
        case 'activities':
          viewType = 'attractions';
          break;
        case 'booking_management':
          viewType = 'booking';
          break;
        default:
          viewType = 'empty';
      }

      state.currentIntent = intent;
      get().navigateToView(viewType, data, { intent });
      return state;
    }, false);
  },

  // Navigate to view - enhanced version of your existing logic
  navigateToView: (viewType, data = {}, metadata = {}) => {
    set(state => {
      const newState: PreviewState = {
        id: `${viewType}-${Date.now()}`,
        viewType,
        viewMode: state.currentState.viewMode,
        data,
        title: generateViewTitle(viewType, data),
        timestamp: new Date(),
        intent: state.currentIntent || '',
        metadata,
      };

      // Add current state to history if it's different
      if (state.currentState.id !== 'initial' && state.currentState.viewType !== viewType) {
        const historyItem: NavigationHistoryItem = {
          state: state.currentState,
          action: `navigated-to-${viewType}`,
          timestamp: new Date(),
        };

        const newHistory = [...state.history.slice(0, state.historyIndex + 1), historyItem];
        if (newHistory.length > state.maxHistorySize) {
          newHistory.shift();
        }

        state.history = newHistory;
        state.historyIndex = newHistory.length - 1;
      }

      state.currentState = newState;
      state.hoveredHotelId = null;
      state.selectedItemId = null;
      return state;
    }, false);
  },

  // Update view data - matches your existing updateViewData pattern
  updateViewData: data => {
    set(state => {
      state.currentState.data = { ...state.currentState.data, ...data };
      state.currentState.title = generateViewTitle(state.currentState.viewType, state.currentState.data);
      state.currentState.timestamp = new Date();
      return state;
    }, false);
  },

  // Update view mode - matches your handleViewToggle
  updateViewMode: mode => {
    set(state => {
      state.currentState.viewMode = mode;
      return state;
    }, false);
  },

  // Navigation methods
  goBack: () => {
    const { historyIndex } = get();
    if (historyIndex > 0) {
      set(state => {
        state.historyIndex -= 1;
        state.currentState = state.history[state.historyIndex].state;
        state.hoveredHotelId = null;
        state.selectedItemId = null;
        return state;
      }, false);
      return true;
    }
    return false;
  },

  goForward: () => {
    const { history, historyIndex } = get();
    if (historyIndex < history.length - 1) {
      set(state => {
        state.historyIndex += 1;
        state.currentState = state.history[state.historyIndex].state;
        state.hoveredHotelId = null;
        state.selectedItemId = null;
        return state;
      }, false);
      return true;
    }
    return false;
  },

  canGoBack: () => {
    const { historyIndex } = get();
    return historyIndex > 0;
  },

  canGoForward: () => {
    const { history, historyIndex } = get();
    return historyIndex < history.length - 1;
  },

  clearHistory: () => {
    set(state => {
      state.history = [];
      state.historyIndex = -1;
      return state;
    }, false);
  },

  // Item interaction - matches your existing hover patterns
  setHoveredHotelId: hotelId => {
    set(state => {
      state.hoveredHotelId = hotelId;
      return state;
    }, false);
  },

  setSelectedItem: (itemId, itemData) => {
    set(state => {
      state.selectedItemId = itemId;
      if (itemData) {
        state.currentState.data.selectedItem = itemData;
      }
      return state;
    }, false);
  },

  // Loading and status - matches your existing patterns
  setLoading: loading => {
    set(state => {
      state.isLoading = loading;
      return state;
    }, false);
  },

  setIsProcessing: (processing, chatId) => {
    set(state => {
      // If chatId provided, update that specific chat's processing state
      const targetChatId = chatId ?? state.currentChatId;
      if (targetChatId) {
        const chat = state.chatSessions.find(c => c.id === targetChatId);
        if (chat) {
          chat.isProcessing = processing;
        }
      }
      // Update global state only if targeting the current chat (or no chatId specified)
      if (!chatId || chatId === state.currentChatId) {
        state.isProcessing = processing;
      }
      return state;
    }, false);
  },

  setIsNewSearchResult: (isNew: boolean) => {
    set(state => {
      state.isNewSearchResult = isNew;
      return state;
    }, false);
  },

  setStatus: (status, chatId) => {
    set(state => {
      // If chatId provided, update that specific chat's status
      const targetChatId = chatId ?? state.currentChatId;
      if (targetChatId) {
        const chat = state.chatSessions.find(c => c.id === targetChatId);
        if (chat) {
          chat.processingStatus = status;
        }
      }
      // Update global state only if targeting the current chat (or no chatId specified)
      if (!chatId || chatId === state.currentChatId) {
        state.status = status;
      }
      return state;
    }, false);
  },

  enqueueLoadingMessage: (message: LoadingMessage, chatId?: string) => {
    const state = get();
    const targetChatId = chatId ?? state.currentChatId;

    // Skip duplicate — already showing or already buffered
    if (message === state.loadingMessage || message === state.pendingLoadingMessage) return;

    // Update the target chat's loading message
    if (targetChatId) {
      set(s => {
        const chat = s.chatSessions.find(c => c.id === targetChatId);
        if (chat) {
          chat.loadingMessage = message;
        }
        return s;
      }, false);
    }

    // Only update global loading message display if this is the current chat
    if (!chatId || chatId === state.currentChatId) {
      // Buffer the incoming message
      set(s => ({ ...s, pendingLoadingMessage: message }), false);

      // If a display timer is already running, let it handle the pending message on expiry
      if (state.loadingMessageTimer !== null) return;

      // Start the display loop — immediately show the first pending message, then
      // set a timer so it stays visible for at least MIN_PHASE_DISPLAY_DURATION_MS.
      const advance = () => {
        set(s => {
          const next = s.pendingLoadingMessage;
          if (next === null) {
            // Queue drained — clear the timer handle
            return { ...s, loadingMessageTimer: null };
          }
          const timer = setTimeout(advance, MIN_PHASE_DISPLAY_DURATION_MS);
          return {
            ...s,
            loadingMessage: next,
            pendingLoadingMessage: null,
            loadingMessageTimer: timer,
          };
        }, false);
      };

      advance();
    }
  },

  clearLoadingMessage: (chatId?: string) => {
    const state = get();
    const targetChatId = chatId ?? state.currentChatId;

    // Clear the target chat's loading message
    if (targetChatId) {
      set(s => {
        const chat = s.chatSessions.find(c => c.id === targetChatId);
        if (chat) {
          chat.loadingMessage = null;
        }
        return s;
      }, false);
    }

    // Only clear global loading message if this is the current chat
    if (!chatId || chatId === state.currentChatId) {
      set(s => {
        if (s.loadingMessageTimer !== null) {
          clearTimeout(s.loadingMessageTimer);
        }
        return { ...s, loadingMessage: null, pendingLoadingMessage: null, loadingMessageTimer: null };
      }, false);
    }
  },

  // Per-chat processing state management - updates all processing-related state for a chat
  setChatProcessingState: (
    chatId: string,
    isProcessing: boolean,
    status?: AgentStatus,
    loadingMessage?: LoadingMessage | null
  ) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        chat.isProcessing = isProcessing;
        if (status !== undefined) {
          chat.processingStatus = status;
        }
        if (loadingMessage !== undefined) {
          chat.loadingMessage = loadingMessage;
        }
      }
      // Sync to global state if this is the current chat
      if (chatId === state.currentChatId) {
        state.isProcessing = isProcessing;
        if (status !== undefined) {
          state.status = status;
        }
        if (loadingMessage !== undefined) {
          state.loadingMessage = loadingMessage;
          // Clear timer if clearing loading message
          if (loadingMessage === null && state.loadingMessageTimer !== null) {
            clearTimeout(state.loadingMessageTimer);
            state.loadingMessageTimer = null;
            state.pendingLoadingMessage = null;
          }
        }
      }
      return state;
    }, false);
  },

  cancelRequest: () => {
    set(state => {
      // Only add cancellation message if there's actually a request being processed
      const wasProcessing = state.isLoading || state.isProcessing;

      // Interrupt TIP and null the guard ref so stale events are ignored
      const currentChatId = state.currentChatId;

      // Clear processing state on the current chat
      if (currentChatId) {
        const currentChat = state.chatSessions.find(c => c.id === currentChatId);
        if (currentChat) {
          currentChat.isProcessing = false;
          currentChat.processingStatus = { status: 'idle' };
          currentChat.loadingMessage = null;
        }
      }

      state.isLoading = false;
      state.isProcessing = false;
      state.status = { status: 'idle' };

      // Clear any in-flight loading message immediately
      if (state.loadingMessageTimer !== null) {
        clearTimeout(state.loadingMessageTimer);
      }
      state.loadingMessage = null;
      state.pendingLoadingMessage = null;
      state.loadingMessageTimer = null;

      // Only add cancellation message if we were actually processing something
      if (wasProcessing && currentChatId) {
        const cancelMessage = {
          id: Date.now().toString(),
          content: USER_CANCELLED_REQUEST_MESSAGE_COPY,
          isUser: false,
          timestamp: new Date(),
          toolsUsed: [],
          intent: MESSAGE_INTENT_USER_CANCELLED_REQUEST,
        };

        // Add to current messages
        state.messages.push(cancelMessage);

        // Add to current chat session
        const currentChat = state.chatSessions.find(c => c.id === currentChatId);
        if (currentChat) {
          currentChat.messages = currentChat.messages || [];
          currentChat.messages.push(cancelMessage);
          currentChat.messageCount++;
          currentChat.updatedAt = new Date();
        }
      }

      return state;
    }, false);
  },

  // Enhanced Chat Session Actions
  createNewChat: () => {
    const currentState = get();

    // First, check if we're already on an empty chat - if so, just stay here (no-op)
    if (currentState.currentChatId) {
      const currentChat = currentState.chatSessions.find(c => c.id === currentState.currentChatId);
      if (currentChat && (!currentChat.messages || currentChat.messages.length === 0)) {
        // Already on an empty chat, no need to do anything
        return currentState.currentChatId;
      }
    }

    // Check for any existing empty chat to reuse
    const existingEmptyChat = currentState.chatSessions.find(chat => !chat.messages || chat.messages.length === 0);

    if (existingEmptyChat) {
      // Reuse existing empty chat instead of creating a new one
      set(state => {
        state.currentChatId = existingEmptyChat.id;
        state.messages = [];
        state.inputValue = '';
        state.currentState = createInitialState();
        state.currentIntent = null;
        state.showChat = true;
        // Reset global processing state for the new chat (it has no processing)
        state.isProcessing = false;
        state.isLoading = false;
        state.status = { status: 'idle' };
        state.loadingMessage = null;
        if (state.loadingMessageTimer !== null) {
          clearTimeout(state.loadingMessageTimer);
          state.loadingMessageTimer = null;
        }
        state.pendingLoadingMessage = null;
        return state;
      }, false);
      return existingEmptyChat.id;
    }

    // No empty chat found - create a new one
    const newChatId = uuidv4();
    set(state => {
      const newChat: ChatSession = {
        id: newChatId,
        title: 'New Chat',
        messages: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        messageCount: 0,
        expiresAt: getChatExpirationDate(),
        tipWorkflowId: undefined,
        tipStreamUrl: undefined,
      };
      state.chatSessions.push(newChat);
      state.currentChatId = newChatId;
      state.messages = [];
      state.inputValue = '';
      // Reset currentState to show static map on new chat
      state.currentState = createInitialState();
      state.currentIntent = null;
      // Switch to chat view on mobile when creating new chat
      state.showChat = true;
      // Reset global processing state for the new chat (it has no processing)
      state.isProcessing = false;
      state.isLoading = false;
      state.status = { status: 'idle' };
      state.loadingMessage = null;
      if (state.loadingMessageTimer !== null) {
        clearTimeout(state.loadingMessageTimer);
        state.loadingMessageTimer = null;
      }
      state.pendingLoadingMessage = null;
      return state;
    }, false);
    return newChatId;
  },

  setChatTipSession: (chatId, tip) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        chat.tipWorkflowId = tip.workflowId;
        chat.tipStreamUrl = tip.streamUrl;
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  clearChatTipSession: chatId => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        chat.tipWorkflowId = undefined;
        chat.tipStreamUrl = undefined;
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  setLastEventId: (chatId, eventId) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        chat.lastEventId = eventId;
      }
      return state;
    }, false);
  },

  setLatestErrorMessageId: (chatId, errorMessageId) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        chat.latestErrorMessageId = errorMessageId;
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  setChatDisabled: (chatId, disabled) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        chat.isDisabled = disabled;
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  // Per-conversation search state actions
  setConversationSearchCriteria: (chatId, criteria) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        // Initialize conversationSearchState if it doesn't exist
        if (!chat.conversationSearchState) {
          chat.conversationSearchState = {
            searchCriteria: { ...DEFAULT_SEARCH_CRITERIA },
            filterState: { ...DEFAULT_FILTER_STATE },
          };
        }
        // Merge the new criteria with existing
        chat.conversationSearchState.searchCriteria = {
          ...chat.conversationSearchState.searchCriteria,
          ...criteria,
        };
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  setConversationFilterState: (chatId, filters) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        // Initialize conversationSearchState if it doesn't exist
        if (!chat.conversationSearchState) {
          chat.conversationSearchState = {
            searchCriteria: { ...DEFAULT_SEARCH_CRITERIA },
            filterState: { ...DEFAULT_FILTER_STATE },
          };
        }
        // Merge the new filters with existing
        chat.conversationSearchState.filterState = {
          ...chat.conversationSearchState.filterState,
          ...filters,
        };
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  setConversationLocationState: (chatId, location) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        // Initialize conversationSearchState if it doesn't exist
        if (!chat.conversationSearchState) {
          chat.conversationSearchState = {
            searchCriteria: { ...DEFAULT_SEARCH_CRITERIA },
            filterState: { ...DEFAULT_FILTER_STATE },
          };
        }
        // Merge the new location with existing
        chat.conversationSearchState.locationState = {
          ...(chat.conversationSearchState.locationState || {}),
          ...location,
        };
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  getConversationSearchState: chatId => {
    const { chatSessions } = get();
    const chat = chatSessions.find(c => c.id === chatId);
    return chat?.conversationSearchState;
  },

  setConversationSearchModified: (chatId, modified) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        if (!chat.conversationSearchState) {
          chat.conversationSearchState = {
            searchCriteria: { ...DEFAULT_SEARCH_CRITERIA },
            filterState: { ...DEFAULT_FILTER_STATE },
          };
        }
        chat.conversationSearchState.hasUserModifiedSearch = modified;
      }
      return state;
    }, false);
  },

  getConversationSearchModified: chatId => {
    const { chatSessions } = get();
    const chat = chatSessions.find(c => c.id === chatId);
    return chat?.conversationSearchState?.hasUserModifiedSearch ?? false;
  },

  // FIXED: Properly handles messages, preview state, and processing state when switching chats
  // Also syncs per-conversation search state to global stores with skip-if-identical optimization
  switchToChat: chatId => {
    // Get current state to check if we're already on this chat
    const currentState = get();
    if (currentState.currentChatId === chatId) {
      return; // Already on this chat, no need to switch
    }

    // Check if we need to sync conversation state before the set() call
    const targetChat = currentState.chatSessions.find(c => c.id === chatId);
    const needsSync = targetChat ? shouldSyncOnConversationSwitch(chatId, targetChat.conversationSearchState) : false;

    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        state.currentChatId = chatId;
        // FIXED: Ensure messages array exists and copy it, handling date conversion
        state.messages = chat.messages
          ? chat.messages.map(msg => ({
              ...msg,
              timestamp: typeof msg.timestamp === 'string' ? new Date(msg.timestamp) : msg.timestamp,
            }))
          : [];
        state.inputValue = '';

        // Reset isNewChatMode when switching to any chat
        // If the chat has messages, it's not a new chat mode
        // If the chat is empty, we still want to show the map (not the static placeholder)
        state.isNewChatMode = false;

        // Sync global processing state from the target chat
        state.isProcessing = chat.isProcessing ?? false;
        state.status = chat.processingStatus ?? { status: 'idle' };
        state.loadingMessage = chat.loadingMessage ?? null;
        // Reset new search result flag when switching chats (no animation for loaded history)
        state.isNewSearchResult = false;
        // Clear any pending loading message timer when switching chats
        if (state.loadingMessageTimer !== null) {
          clearTimeout(state.loadingMessageTimer);
          state.loadingMessageTimer = null;
        }
        state.pendingLoadingMessage = null;
        // Also sync isLoading based on processing state
        state.isLoading = chat.isProcessing ?? false;

        // Find the latest message with preview data to update the current state
        const latestMessageWithPreview = (chat.messages || [])
          .slice() // Create a copy to avoid mutating original
          .reverse() // Start from the latest messages
          .find(msg => msg.hasPreview && msg.previewData);

        if (latestMessageWithPreview && latestMessageWithPreview.previewData) {
          // Update the current state to match the latest preview
          state.currentState = {
            id: `${latestMessageWithPreview.previewData.viewType}-${Date.now()}`,
            viewType: latestMessageWithPreview.previewData.viewType,
            viewMode: state.currentState.viewMode, // Keep current view mode (grid/map)
            data: latestMessageWithPreview.previewData.data,
            title: latestMessageWithPreview.previewData.title,
            timestamp: new Date(),
            intent: latestMessageWithPreview.intent,
            metadata: {}, // Could be expanded with additional metadata if needed
          };

          // Update current intent to match the message
          state.currentIntent = latestMessageWithPreview.intent || null;
        } else {
          // If no message with preview data found, reset to initial state
          state.currentState = createInitialState();
          state.currentIntent = null;
        }
      }
      return state;
    }, false);

    // Sync conversation search state to global stores (outside of set() to avoid circular deps)
    // Only sync if the fingerprint comparison determined it's needed
    if (needsSync && targetChat) {
      syncConversationStateToGlobalStores(chatId, targetChat.conversationSearchState);
    }
  },

  deleteChat: chatId => {
    // Clear fingerprint cache for the deleted chat
    clearFingerprintCache(chatId);

    set(state => {
      state.chatSessions = state.chatSessions.filter(c => c.id !== chatId);
      if (state.currentChatId === chatId) {
        // FIXED: If deleting current chat, reset everything
        state.currentChatId = null;
        state.messages = [];
        state.inputValue = '';
        state.currentState = createInitialState();
        state.currentIntent = null;
      }
      return state;
    }, false);
  },

  updateChatTitle: (chatId, title) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat) {
        chat.title = title.trim().slice(0, 50);
        chat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  duplicateChat: chatId => {
    const { chatSessions } = get();
    const originalChat = chatSessions.find(c => c.id === chatId);
    if (!originalChat) return null;

    const newChatId = Date.now().toString();
    const duplicatedChat: ChatSession = {
      id: newChatId,
      title: `${originalChat.title} (Copy)`,
      messages: [...originalChat.messages], // Copy messages
      createdAt: new Date(),
      updatedAt: new Date(),
      messageCount: originalChat.messageCount,
      expiresAt: getChatExpirationDate(),
    };

    // Note: this store's `set` only supports 1-2 args; don't pass a devtools action name here.
    set(state => {
      state.chatSessions.push(duplicatedChat);
      state.currentChatId = newChatId;
      state.messages = [...duplicatedChat.messages];
      state.inputValue = '';

      // FIXED: Find the latest message with preview data to update the current state
      const latestMessageWithPreview = duplicatedChat.messages
        .slice()
        .reverse()
        .find(msg => msg.hasPreview && msg.previewData);

      if (latestMessageWithPreview?.previewData) {
        // Update preview to match latest message
        state.currentState = {
          id: `${latestMessageWithPreview.previewData.viewType}-${Date.now()}`,
          viewType: latestMessageWithPreview.previewData.viewType,
          viewMode: state.currentState.viewMode,
          data: latestMessageWithPreview.previewData.data,
          title: latestMessageWithPreview.previewData.title,
          timestamp: new Date(),
          intent: latestMessageWithPreview.intent,
        };
        state.currentIntent = latestMessageWithPreview.intent || null;
      } else {
        // No preview data found, reset to initial
        state.currentState = createInitialState();
        state.currentIntent = null;
      }

      return state;
    }, false);

    return newChatId;
  },

  getCurrentChat: () => {
    const { chatSessions, currentChatId } = get();
    return chatSessions.find(c => c.id === currentChatId) || null;
  },

  // FIXED: Chat integration - properly syncs messages to both current and chat session
  addMessage: message => {
    set(state => {
      const chatId = state.currentChatId;
      if (chatId) {
        // delegate to targeted writer for consistent behavior
        // (we still keep this action for existing callers)
        // NOTE: we cannot call get().addMessageToChat inside set() safely in all immer modes,
        // so we inline the minimal behavior here.
        state.messages.push(message);
        const currentChat = state.chatSessions.find(c => c.id === chatId);
        if (currentChat) {
          currentChat.messages = currentChat.messages || [];
          currentChat.messages.push(message);
          currentChat.messageCount++;
          currentChat.updatedAt = new Date();
          if (message.isUser && currentChat.messageCount === 1 && currentChat.title === 'New Chat') {
            currentChat.title = get().generateChatTitle(message.content);
          }
        }
        // Reset new chat mode when messages are added
        state.isNewChatMode = false;
      }
      return state;
    }, false);
  },

  removeMessage: messageId => {
    set(state => {
      const chatId = state.currentChatId;
      // Remove from current messages array
      state.messages = state.messages.filter(m => m.id !== messageId);
      // Also remove from the chat session
      if (chatId) {
        const currentChat = state.chatSessions.find(c => c.id === chatId);
        if (currentChat) {
          currentChat.messages = (currentChat.messages || []).filter(m => m.id !== messageId);
          currentChat.messageCount = Math.max(0, currentChat.messageCount - 1);
          currentChat.updatedAt = new Date();
        }
      }
      return state;
    }, false);
  },

  addMessageToChat: (chatId, message) => {
    set(state => {
      const chat = state.chatSessions.find(c => c.id === chatId);
      if (!chat) return state;

      // Always append to the targeted chat session
      chat.messages = chat.messages || [];
      chat.messages.push(message);
      chat.messageCount++;
      chat.updatedAt = new Date();

      // Keep current messages in sync only if this chat is active
      if (state.currentChatId === chatId) {
        state.messages.push(message);
      }

      // Auto-generate title from first user message
      if (message.isUser && chat.messageCount === 1 && chat.title === 'New Chat') {
        chat.title = get().generateChatTitle(message.content);
      }

      return state;
    }, false);
  },

  setMessageThumbFeedback: (chatId, messageId, feedback) => {
    set(state => {
      const patchMessage = (m: Message) =>
        m.id === messageId && !m.isUser && !m.thumbFeedback ? { ...m, thumbFeedback: feedback } : m;

      const chat = state.chatSessions.find(c => c.id === chatId);
      if (chat?.messages?.length) {
        chat.messages = chat.messages.map(patchMessage);
        chat.updatedAt = new Date();
      }
      if (state.currentChatId === chatId && state.messages.length) {
        state.messages = state.messages.map(patchMessage);
      }
      return state;
    }, false);
  },

  setInputValue: value => {
    set(state => {
      state.inputValue = value;
      return state;
    }, false);
  },

  // FIXED: Enhanced chat actions with navigation support
  addAIMessageWithNavigation: (
    content: string,
    viewType: ViewType,
    viewData: ViewData,
    toolsUsed?: string[],
    intent?: string
  ) => {
    set(state => {
      const itemCount = getItemCountForViewType(viewType, viewData);
      const navigationButton = createNavigationButton(viewType, viewData, itemCount);

      const aiMessage: Message = {
        id: Date.now().toString() + '-ai',
        content,
        isUser: false,
        timestamp: new Date(),
        toolsUsed,
        intent,
        hasPreview: true,
        previewData: {
          viewType,
          data: viewData,
          title: generateViewTitle(viewType, viewData),
          itemCount,
        },
        navigationButton,
      };

      // Add to current messages
      state.messages.push(aiMessage);

      // Find or create current chat session
      let currentChat = state.chatSessions.find(c => c.id === state.currentChatId);
      if (!currentChat) {
        const newChatId = Date.now().toString();
        const newChat: ChatSession = {
          id: newChatId,
          title: 'New Chat',
          messages: [],
          createdAt: new Date(),
          updatedAt: new Date(),
          messageCount: 0,
          expiresAt: getChatExpirationDate(),
        };
        state.chatSessions.push(newChat);
        state.currentChatId = newChatId;
        currentChat = newChat;
      }

      // Add message to chat session
      if (currentChat) {
        if (!currentChat.messages) {
          currentChat.messages = [];
        }
        currentChat.messages.push(aiMessage);
        currentChat.messageCount++;
        currentChat.updatedAt = new Date();
      }
      return state;
    }, false);
  },

  // Navigate to message preview - for when user clicks navigation button
  navigateToMessagePreview: (messageId: string) => {
    const message = get().messages.find(msg => msg.id === messageId);
    if (message && message.previewData) {
      get().navigateToView(message.previewData.viewType, message.previewData.data, { intent: message.intent });
    }
  },

  clearMessages: () => {
    set(state => {
      state.messages = [];
      return state;
    }, false);
  },

  // UI state management - matches your existing responsive handling
  setShowChat: show => {
    set(state => {
      state.showChat = show;
      return state;
    }, false);
  },

  setIsMobile: mobile => {
    set(state => {
      state.isMobile = mobile;
      return state;
    }, false);
  },

  setIsSidebarOpen: open => {
    set(state => {
      state.isSidebarOpen = open;
      return state;
    }, false);
  },

  setIsNewChatMode: isNewChatMode => {
    set(state => {
      state.isNewChatMode = isNewChatMode;
      return state;
    }, false);
  },

  setIsSettingsOpen: open => {
    set(state => {
      state.isSettingsOpen = open;
      return state;
    }, false);
  },

  // FIXED: Utility methods
  reset: () => {
    set(state => {
      state.currentState = createInitialState();
      state.history = [];
      state.historyIndex = -1;
      state.isLoading = false;
      state.isProcessing = false;
      state.status = { status: 'idle' };
      state.hoveredHotelId = null;
      state.selectedItemId = null;
      state.currentIntent = null;
      state.chatSessions = [];
      state.currentChatId = null;
      state.messages = [];
      state.inputValue = '';
      state.propertyMediaById = {};
      return state;
    }, false);
  },

  getCurrentViewTitle: () => {
    const { currentState } = get();
    return generateViewTitle(currentState.viewType, currentState.data);
  },

  getCurrentItemCount: () => {
    const { currentState } = get();
    const { data, viewType } = currentState;

    switch (viewType) {
      case 'hotel-list':
        return data.totalResults || 0;
      case 'dining':
        return data.restaurants?.length || 0;
      case 'attractions':
        return data.attractions?.length || 0;
      default:
        return 0;
    }
  },

  // FIXED: New Chat Management Utilities
  generateChatTitle: (firstMessage: string) => {
    // Generate a more meaningful title from the first message
    const cleanMessage = firstMessage.trim();
    if (cleanMessage.length <= 50) {
      return cleanMessage;
    }
    // Take first 47 characters and add ellipsis
    return cleanMessage.substring(0, 47) + '...';
  },

  getRecentChats: (limit?: number) => {
    const { chatSessions } = get();
    const filtered = chatSessions
      .filter(chat => chat.messageCount > 0) // Only show chats with messages
      .sort((a, b) => {
        // Sort by creation date - newest first
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      });
    // If limit is provided, slice; otherwise return all
    return limit ? filtered.slice(0, limit) : filtered;
  },

  searchChats: (query: string) => {
    const { chatSessions } = get();
    return chatSessions.filter(c => c.title.toLowerCase().includes(query.toLowerCase()));
  },
  // Experiments actions
  toggleExperiment: (key: string) => {
    set(state => {
      if (key in state.experiments) {
        const newValue = !state.experiments[key as keyof typeof state.experiments];
        state.experiments[key as keyof typeof state.experiments] = newValue;

        // Handle mutual exclusivity: useNLSSearch and useNLSUXL cannot both be true
        if (newValue && key === 'useNLSSearch' && state.experiments.useNLSUXL) {
          state.experiments.useNLSUXL = false;
        } else if (newValue && key === 'useNLSUXL' && state.experiments.useNLSSearch) {
          state.experiments.useNLSSearch = false;
        }
      }
      return state;
    }, false);
  },

  setExperiments: newExperiments => {
    set(state => {
      state.experiments = { ...state.experiments, ...newExperiments };
      return state;
    }, false);
  },

  getActiveExperiments: () => {
    const { experiments } = get();
    return Object.entries(experiments)
      .filter(([_, value]) => value === true)
      .map(([key, _]) => key);
  },
});

// Helper function - matches your existing getPreviewTitle logic
function generateViewTitle(viewType: ViewType, data: ViewData): string {
  switch (viewType) {
    case 'hotel-list': {
      return (
        data.rawData?.data?.search?.conversationalSearch?.searchByGeolocation?.edges?.length || data.totalResults || 0
      );
    }
    case 'hotel-detail':
      return 'Hotel Quick View';
    case 'dining': {
      const count = data.restaurants?.length || 0;
      return `Restaurants (${count} found)`;
    }
    case 'attractions': {
      const count = data.attractions?.length || 0;
      return `Attractions (${count} found)`;
    }
    case 'booking':
    case 'booking_management':
      return 'Booking Management';
    case 'map':
      return 'Map View';
    case 'empty':
    default:
      return 'Your Next Trip Starts Here';
  }
}

// Helper function to get item count for different view types
function getItemCountForViewType(viewType: ViewType, data: ViewData): number {
  switch (viewType) {
    case 'hotel-list':
      return (
        data.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.total || data.totalResults || 0
      );
    case 'dining':
      return data.restaurants?.length || 0;
    case 'attractions':
      return data.attractions?.length || 0;
    default:
      return 0;
  }
}

// Helper function to create navigation buttons for messages
function createNavigationButton(viewType: ViewType, data: ViewData, itemCount: number) {
  switch (viewType) {
    case 'hotel-list':
      return {
        label: `See all ${itemCount} properties`,
        action: () => {
          const store = useMarriottAIPreviewStore.getState();
          store.navigateToView('hotel-list', data);
        },
        variant: 'outline' as const,
      };
    case 'hotel-detail':
      return {
        label: 'View Hotel Details',
        action: () => {
          const store = useMarriottAIPreviewStore.getState();
          store.navigateToView('hotel-detail', data);
        },
        variant: 'primary' as const,
        icon: '🏨',
      };
    case 'dining':
      return {
        label: `View ${itemCount} Restaurants`,
        action: () => {
          const store = useMarriottAIPreviewStore.getState();
          store.navigateToView('dining', data);
        },
        variant: 'primary' as const,
        icon: '🍽️',
      };
    case 'attractions':
      return {
        label: `View ${itemCount} Attractions`,
        action: () => {
          const store = useMarriottAIPreviewStore.getState();
          store.navigateToView('attractions', data);
        },
        variant: 'primary' as const,
        icon: '🎯',
      };
    case 'booking':
    case 'booking_management':
      return {
        label: 'Manage Bookings',
        action: () => {
          const store = useMarriottAIPreviewStore.getState();
          store.navigateToView('booking', data);
        },
        variant: 'secondary' as const,
        icon: '📋',
      };
    case 'map':
      return {
        label: 'View on Map',
        action: () => {
          const store = useMarriottAIPreviewStore.getState();
          store.navigateToView(store.currentState.viewType, data);
          store.updateViewMode('map');
        },
        variant: 'outline' as const,
        icon: '🗺️',
      };
    default:
      return undefined;
  }
}

// FIXED: Store options with persistence - now properly persists messages
// NOTE: `createAppStore`'s StoreOptions typing is stricter than zustand's persist partialize
// (it expects full-state return), but at runtime partialize should return a subset.
// We intentionally loosen typing here to prevent TS errors while keeping persistence lean.

/**
 * Get the effective consumer ID for storage operations.
 * In local development (IS_LOCAL_DEV === 'true'), uses a fixed test ID.
 * In production, uses the actual consumer ID from the store.
 */
const getEffectiveConsumerId = (): string | null => {
  const consumerId = useMarriottAIPreviewStore.getState().consumerId;
  if (consumerId) return consumerId;

  // In local dev, use a fixed dev consumer ID for testing
  const isLocalDev = useClientEnvVarsStore.getState().envVarsObject['IS_LOCAL_DEV'] === 'true';
  if (isLocalDev) {
    return getLocalDevConsumerId();
  }

  return null;
};

// Flag to track if hydration has completed - prevents overwriting stored data before it's loaded
let hasHydrated = false;
// Track last written value to skip redundant writes (prevents re-encryption on every state change)
let lastWrittenValue: string | null = null;

// Encrypted per-user IndexedDB adapter.
// The storage key includes consumerID so different users on the same device
// each have their own isolated, independently encrypted history blob.
// consumerId is read lazily at call time (set via setConsumerId in chat.tsx).
// In local dev, uses LOCAL_DEV_CONSUMER_ID when no real consumer ID is available.
const encryptedChatStorage = {
  getItem: async (_name: string): Promise<string | null> => {
    const consumerId = getEffectiveConsumerId();
    if (!consumerId) return null;
    const raw = await idbGet(await chatStorageKey(consumerId));
    if (!raw) {
      hasHydrated = true;
      return null;
    }
    try {
      const decrypted = await decryptChatHistory(raw, consumerId);
      const result = JSON.stringify(decrypted);
      hasHydrated = true;
      lastWrittenValue = result;
      return result;
    } catch (e) {
      hasHydrated = true;
      return null;
    }
  },
  setItem: async (_name: string, value: string): Promise<void> => {
    if (!hasHydrated) return;

    if (value === lastWrittenValue) {
      return;
    }

    const consumerId = getEffectiveConsumerId();
    if (!consumerId) return;

    try {
      const encrypted = await encryptChatHistory(JSON.parse(value), consumerId);
      await idbSet(await chatStorageKey(consumerId), encrypted);
      lastWrittenValue = value; // Track what we wrote
    } catch (e) {
      if (isStorageError(e)) {
        // Log storage error but don't block - data is safe in memory
        console.error('[Storage] Failed to save chat history due to storage limit:', e);
        return;
      }
      // Re-throw non-storage errors
      throw e;
    }
  },
  removeItem: async (_name: string): Promise<void> => {
    const consumerId = getEffectiveConsumerId();
    if (consumerId) await idbDelete(await chatStorageKey(consumerId));
  },
};

const storeOptions: any = {
  usePersistentStore: true,
  persistentStoreName: 'marriott-ai-chat', // key is overridden per-user by encryptedChatStorage
  customStorage: encryptedChatStorage,
  devToolsName: 'MarriottAIPreviewStore',
  skipHydration: true, // Prevent auto-hydration before consumerId is set
  // Filter expired sessions and mark hydration complete
  onRehydrateStorage: () => {
    // This outer function runs BEFORE hydration starts
    return (state: any, error: any) => {
      // This inner function runs AFTER hydration completes
      if (error) {
        console.error('[Storage] Hydration failed:', error);
      }
      // Mark hydration as complete - use setState to trigger re-render
      useMarriottAIPreviewStore.setState({ _hasHydrated: true });

      // Filter expired sessions if state was loaded
      if (state) {
        const now = new Date();
        const filteredSessions = (state.chatSessions || []).filter(
          (c: any) => c.expiresAt && new Date(c.expiresAt) > now
        );
        if (filteredSessions.length !== (state.chatSessions || []).length) {
          useMarriottAIPreviewStore.setState({ chatSessions: filteredSessions });
        }
      }
    };
  },
  partialize: ((state: MarriottAIPreviewStore) => {
    const now = new Date();
    // Find the most recent empty session with TIP credentials to preserve for reuse on refresh
    const emptySessionWithTip = state.chatSessions
      .filter(
        c => c.messageCount === 0 && c.tipWorkflowId && c.tipStreamUrl && c.expiresAt && new Date(c.expiresAt) > now
      )
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

    return {
      // NOTE: currentChatId is intentionally NOT persisted - we always show landing page on refresh
      // All non-empty sessions + ONE empty session with TIP credentials (for reuse on refresh)
      chatSessions: state.chatSessions
        .filter(
          c =>
            (c.messageCount > 0 || (emptySessionWithTip && c.id === emptySessionWithTip.id)) &&
            c.expiresAt &&
            new Date(c.expiresAt) > now
        )
        // Keep original creation order - newest chats first (already sorted by createdAt desc from filter)
        .map(
          ({
            id,
            title,
            messageCount,
            tipWorkflowId,
            tipStreamUrl,
            lastEventId,
            createdAt,
            updatedAt,
            expiresAt,
            messages,
            latestErrorMessageId,
            isDisabled,
            conversationSearchState,
          }) => ({
            id,
            title,
            messageCount,
            tipWorkflowId,
            tipStreamUrl,
            lastEventId,
            createdAt,
            updatedAt,
            expiresAt,
            latestErrorMessageId,
            isDisabled,
            // Only persist conversationSearchState if it has non-default values
            // This reduces IndexedDB storage size
            // NOTE: filterState is intentionally stripped - we only persist searchCriteria and locationState
            // because the stored search results are from TIP, not filtered results
            conversationSearchState: hasNonDefaultState(conversationSearchState)
              ? {
                  searchCriteria: conversationSearchState!.searchCriteria,
                  locationState: conversationSearchState!.locationState,
                  // filterState is intentionally omitted to maintain consistency with stored TIP results
                }
              : undefined,
            // Strip non-serializable fields (navigationButton contains functions).
            // Keep previewData and uiData for restoring chat history with results.
            messages: (messages ?? []).map(
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ({ navigationButton: _n, ...safe }) => ({
                ...safe,
                content: safe.isUser ? redact(safe.content).redactedText : safe.content,
              })
            ),
          })
        ) as any,
      experiments: state.experiments,
      usePersonalization: state.usePersonalization,
      selectedMemberLevel: state.selectedMemberLevel,
    } as any;
  }) as any,
};

// Create and export the store
export const useMarriottAIPreviewStore = createAppStore(createMarriottAIPreviewStore, storeOptions);

// Selector hooks for optimal performance - matching your component structure
export const useCurrentView = () => useMarriottAIPreviewStore(state => state.currentState);
export const useViewData = () => useMarriottAIPreviewStore(state => state.currentState.data);
export const useViewMode = () => useMarriottAIPreviewStore(state => state.currentState.viewMode);
export const useHoveredHotelId = () => useMarriottAIPreviewStore(state => state.hoveredHotelId);
export const useLoadingState = () =>
  useMarriottAIPreviewStore(state => ({
    isLoading: state.isLoading,
    isProcessing: state.isProcessing,
    status: state.status,
  }));

export const useProcessingState = () => useMarriottAIPreviewStore(state => state.isProcessing);

export const useLoadingMessage = () => useMarriottAIPreviewStore(state => state.loadingMessage);

// Per-chat processing state selectors
export const useIsCurrentChatProcessing = () =>
  useMarriottAIPreviewStore(state => {
    const currentChat = state.chatSessions.find(c => c.id === state.currentChatId);
    return currentChat?.isProcessing ?? false;
  });

export const useCurrentChatLoadingMessage = () =>
  useMarriottAIPreviewStore(state => {
    const currentChat = state.chatSessions.find(c => c.id === state.currentChatId);
    return currentChat?.loadingMessage ?? null;
  });

export const useCurrentChatProcessingStatus = () =>
  useMarriottAIPreviewStore(state => {
    const currentChat = state.chatSessions.find(c => c.id === state.currentChatId);
    return currentChat?.processingStatus ?? { status: 'idle' as const };
  });

export const useIsChatDisabled = () =>
  useMarriottAIPreviewStore(state => {
    const currentChat = state.chatSessions.find(c => c.id === state.currentChatId);
    return currentChat?.isDisabled ?? false;
  });

export const useChatState = () =>
  useMarriottAIPreviewStore(state => ({
    messages: state.messages,
    inputValue: state.inputValue,
  }));
export const useUIState = () =>
  useMarriottAIPreviewStore(state => ({
    showChat: state.showChat,
    isMobile: state.isMobile,
    isSidebarOpen: state.isSidebarOpen,
    isNewChatMode: state.isNewChatMode,
    isSettingsOpen: state.isSettingsOpen,
  }));

// Action selectors
export const useNavigationActions = () =>
  useMarriottAIPreviewStore(state => ({
    navigateToView: state.navigateToView,
    updateViewData: state.updateViewData,
    updateViewMode: state.updateViewMode,
    goBack: state.goBack,
    goForward: state.goForward,
    canGoBack: state.canGoBack(),
    canGoForward: state.canGoForward(),
    clearHistory: state.clearHistory,
  }));

export const useItemActions = () =>
  useMarriottAIPreviewStore(state => ({
    setHoveredHotelId: state.setHoveredHotelId,
    setSelectedItem: state.setSelectedItem,
  }));

export const useAPIActions = () =>
  useMarriottAIPreviewStore(state => ({
    handleAPIResponse: state.handleAPIResponse,
    updateViewFromIntent: state.updateViewFromIntent,
    setLoading: state.setLoading,
    setIsProcessing: state.setIsProcessing,
    setStatus: state.setStatus,
    cancelRequest: state.cancelRequest,
    enqueueLoadingMessage: state.enqueueLoadingMessage,
    clearLoadingMessage: state.clearLoadingMessage,
    setChatProcessingState: state.setChatProcessingState,
  }));

export const useChatActions = () =>
  useMarriottAIPreviewStore(state => ({
    addMessage: state.addMessage,
    addMessageToChat: state.addMessageToChat,
    setMessageThumbFeedback: state.setMessageThumbFeedback,
    addAIMessageWithNavigation: state.addAIMessageWithNavigation,
    navigateToMessagePreview: state.navigateToMessagePreview,
    setInputValue: state.setInputValue,
    clearMessages: state.clearMessages,
    removeMessage: state.removeMessage,
  }));

export const useUIActions = () =>
  useMarriottAIPreviewStore(state => ({
    setShowChat: state.setShowChat,
    setIsMobile: state.setIsMobile,
    setIsSidebarOpen: state.setIsSidebarOpen,
    setIsNewChatMode: state.setIsNewChatMode,
    setIsSettingsOpen: state.setIsSettingsOpen,
  }));

export const useChatSessionActions = () =>
  useMarriottAIPreviewStore(state => ({
    createNewChat: state.createNewChat,
    switchToChat: state.switchToChat,
    deleteChat: state.deleteChat,
    updateChatTitle: state.updateChatTitle,
    setChatTipSession: state.setChatTipSession,
    clearChatTipSession: state.clearChatTipSession,
    setLastEventId: state.setLastEventId,
    setLatestErrorMessageId: state.setLatestErrorMessageId,
    setChatDisabled: state.setChatDisabled,
    duplicateChat: state.duplicateChat,
    getCurrentChat: state.getCurrentChat,
    getRecentChats: state.getRecentChats,
    searchChats: state.searchChats,
    generateChatTitle: state.generateChatTitle,
  }));
export const useExperimentsActions = () =>
  useMarriottAIPreviewStore(state => ({
    experiments: state.experiments,
    toggleExperiment: state.toggleExperiment,
    setExperiments: state.setExperiments,
    getActiveExperiments: state.getActiveExperiments,
  }));
export const usePersonalizationState = () =>
  useMarriottAIPreviewStore(state => ({
    usePersonalization: state.usePersonalization,
    selectedMemberLevel: state.selectedMemberLevel,
    mockProfileData: state.mockProfileData,
    fullProfileData: state.fullProfileData,
    userPreferences: state.userPreferences,
    recentActivity: state.recentActivity,
    personalizedSuggestions: state.personalizedSuggestions,
    consumerId: state.consumerId,
  }));

export const usePersonalizationActions = () =>
  useMarriottAIPreviewStore(state => ({
    togglePersonalization: state.togglePersonalization,
    setSelectedMemberLevel: state.setSelectedMemberLevel,
    setMockProfileData: state.setMockProfileData,
    loadUserProfile: state.loadUserProfile,
    setFullProfileData: state.setFullProfileData,
    setUserPreferences: state.setUserPreferences,
    setRecentActivity: state.setRecentActivity,
    setPersonalizedSuggestions: state.setPersonalizedSuggestions,
  }));

// Sign-in drawer model selector (for OChatSignedOut component)
export const useSignInDrawerModel = () => useMarriottAIPreviewStore(state => state.signInDrawerModel);
